import { nextTick, ref } from 'vue'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'
import type {
  Stripe,
  StripeCheckoutElementsSdk,
  StripeCheckoutExpressCheckoutElement,
  StripeExpressCheckoutElementConfirmEvent,
  StripePaymentElement,
} from '@stripe/stripe-js'
import { useDashboardToast } from '@/composables/useDashboardToast'
import { refreshDataAfterUpgrade } from '@/composables/refreshDataAfterUpgrade'
import {
  getStripe,
  isMockStripeCheckout,
} from '@/helpers/swiftaidGivingCheckout'
import { sendExceptionToAnalytics } from '@/services/googleAnalytics/errorHandling'

export type UpgradeModalStep = 'pitch' | 'checkout' | 'processing' | 'success'

const isOpen = ref(false)
const step = ref<UpgradeModalStep>('pitch')
const sessionId = ref<string | null>(null)
const clientSecret = ref<string | null>(null)
const checkoutError = ref<string | null>(null)
const nameError = ref<string | null>(null)
const isProcessing = ref(false)
const paymentElementReady = ref(false)
// Whether the Express Checkout Element resolved at least one wallet (Apple Pay /
// Google Pay / PayPal) for this device. Stays false when none are available, so the
// modal hides the express section and its divider entirely.
const expressCheckoutAvailable = ref(false)
const selectedPaymentMethod = ref<string | null>(null)
const cardholderName = ref('')
// Billing country for the card, GB default: Gift Aid donors are UK taxpayers, but the
// card itself can be issued elsewhere. Owned by us because the cardholder name cannot be
// set without a country (see confirmPayment).
const cardholderCountry = ref('GB')

let stripe: Stripe | null = null
let checkout: StripeCheckoutElementsSdk | null = null
let activePaymentElement: StripePaymentElement | null = null
let activeExpressCheckoutElement: StripeCheckoutExpressCheckoutElement | null =
  null
let autoAdvanced = false

const PLAN_POLL_ATTEMPTS = 6
const PLAN_POLL_INTERVAL_MS = 2500

const resetSelectedPaymentMethod = () => {
  selectedPaymentMethod.value = null
  nameError.value = null
  autoAdvanced = false
}

const destroyCheckoutElements = () => {
  const paymentElement = activePaymentElement
  const expressCheckoutElement = activeExpressCheckoutElement
  activePaymentElement = null
  activeExpressCheckoutElement = null
  paymentElement?.destroy()
  expressCheckoutElement?.destroy()
}

const focusCardholderName = () => {
  const input = document.getElementById(
    'upgrade-cardholder-name'
  ) as HTMLInputElement | null
  if (input?.isConnected) input.focus()
}

const reportCheckoutFailure = (
  stage: 'loadActions' | 'confirm',
  method: string
) => {
  sendExceptionToAnalytics(
    `/src/composables/useUpgradeModal.ts#confirmPayment: ${stage} failed for payment method ${method}`
  )
}

/**
 * Upgrade modal: pitch → checkout → success, with the plan re-read from
 * GET /me/plan. The real flow mounts a Stripe Payment Element in-modal via the
 * Checkout Elements SDK (Checkout Sessions API, ui_mode: custom) and confirms the
 * payment without leaving the dashboard, so Stripe owns the subscription lifecycle;
 * mock (Mirage) mode simulates the same steps without Stripe.js.
 */
export function useUpgradeModal() {
  const store = useStore()
  const { t } = useI18n()
  const { showToast } = useDashboardToast()

  const resetModal = () => {
    destroyCheckoutElements()
    step.value = 'pitch'
    sessionId.value = null
    clientSecret.value = null
    checkoutError.value = null
    resetSelectedPaymentMethod()
    isProcessing.value = false
    paymentElementReady.value = false
    expressCheckoutAvailable.value = false
    cardholderName.value = ''
    cardholderCountry.value = 'GB'
    stripe = null
    checkout = null
  }

  const openUpgradeModal = () => {
    resetModal()
    isOpen.value = true
  }

  const closeUpgradeModal = () => {
    isOpen.value = false
    resetModal()
  }

  const startCheckout = async () => {
    checkoutError.value = null
    isProcessing.value = true
    step.value = 'processing'
    try {
      const session = await store.dispatch('createPaymentSession')
      sessionId.value = session.id
      clientSecret.value = session.clientSecret ?? null
      step.value = 'checkout'
    } catch {
      checkoutError.value = t('Dashboard.UpgradeModal.Checkout.ErrorStart')
      step.value = 'pitch'
    } finally {
      isProcessing.value = false
    }
  }

  /**
   * Mounts the Express Checkout Element (wallets) and the Payment Element (card) into
   * the checkout step's containers, both driven by the same Checkout Session. Called by
   * the modal once the checkout markup is in the DOM (real flow only).
   */
  const mountCheckoutElements = async (
    paymentContainer: HTMLElement,
    expressContainer: HTMLElement
  ) => {
    const mountClientSecret = clientSecret.value
    destroyCheckoutElements()
    resetSelectedPaymentMethod()
    checkout = null
    paymentElementReady.value = false
    expressCheckoutAvailable.value = false
    if (!mountClientSecret) return

    const canContinueMount = () =>
      isOpen.value &&
      step.value === 'checkout' &&
      clientSecret.value === mountClientSecret &&
      paymentContainer.isConnected

    try {
      const loadedStripe = await getStripe()
      if (!canContinueMount()) return
      if (!loadedStripe) throw new Error('Stripe.js failed to load')
      stripe = loadedStripe
      // Checkout Elements SDK (Checkout Sessions API, ui_mode: custom). The client
      // secret is the Checkout Session's secret, so Stripe drives the subscription
      // lifecycle; initCheckoutElementsSdk returns the SDK synchronously (not a Promise).
      //
      // The customer email is a SESSION-level field: the backend already set it on the
      // session (customer_email / an attached Customer), so the Payment Element does not
      // collect name/email and there is nothing to re-supply at confirm time — the old
      // fields.billingDetails 'never' + payment_method_data.billing_details gymnastics
      // are gone. We must NOT pass defaultValues.email here: Stripe.js would call
      // updateEmail on a session that already has one and throw an IntegrationError.
      checkout = stripe.initCheckoutElementsSdk({
        clientSecret: mountClientSecret,
        elementsOptions: {
          // Load the app's font into Stripe's iframe (same Google Fonts URL as
          // index.html) so the element's labels/inputs render in Figtree too.
          fonts: [
            {
              cssSrc:
                'https://fonts.googleapis.com/css2?family=Figtree:ital,wght@0,300..900;1,300..700&display=swap',
            },
          ],
          appearance: {
            variables: {
              colorPrimary: '#00a8f4',
              colorText: '#002d43',
              colorDanger: '#d1233a',
              borderRadius: '5px',
              fontFamily: 'Figtree, ui-sans-serif, system-ui, sans-serif',
            },
            // Match the element's field labels to the modal's own sa-form-label
            // styling so Stripe-rendered and app-rendered fields read as one form.
            rules: {
              '.Label': {
                fontSize: '13px',
                fontWeight: '600',
                color: '#335163',
                marginBottom: '6px',
              },
            },
          },
        },
      })

      // Wallet buttons (Apple Pay / Google Pay / PayPal). Confirmation is driven by
      // the element's own 'confirm' event — the wallet supplies its own billing
      // details, so unlike the card path we pass none of our name/country fields.
      const expressCheckoutElement = checkout.createExpressCheckoutElement({
        buttonHeight: undefined,
        buttonTheme: undefined,
        buttonType: {
          applePay: 'subscribe',
          googlePay: 'subscribe',
        },
        layout: {
          overflow: 'never',
        },
        paymentMethodOrder: ['applePay', 'googlePay', 'paypal'],
        paymentMethods: {
          applePay: 'always',
          googlePay: 'always',
          paypal: 'never',
        },
      })
      activeExpressCheckoutElement = expressCheckoutElement
      // availablePaymentMethods is undefined when the device offers no wallet; only
      // when available does the modal reveal the express section (and its "or"
      // divider).
      expressCheckoutElement.on('ready', (event) => {
        if (activeExpressCheckoutElement !== expressCheckoutElement) return
        expressCheckoutAvailable.value = Boolean(event.availablePaymentMethods)
      })
      expressCheckoutElement.on('loaderror', () => {
        if (activeExpressCheckoutElement !== expressCheckoutElement) return
        expressCheckoutAvailable.value = false
      })
      expressCheckoutElement.on('confirm', (event) => {
        if (activeExpressCheckoutElement !== expressCheckoutElement) return
        return confirmExpressPayment(event)
      })
      expressCheckoutElement.mount(expressContainer)

      const paymentElement = checkout.createPaymentElement({
        layout: 'tabs',
        paymentMethodOrder: ['card'],
        // The subscription mandate line is rendered by us AFTER our name/country
        // fields (the element would otherwise wedge it between its card fields and
        // ours) — see the SubscriptionTerms paragraph in UpgradeModal.vue.
        terms: {
          card: 'never',
          paypal: 'never',
          applePay: 'never',
          googlePay: 'never',
        },
        // We render our own name/country fields (card tab only) and pass them to
        // confirm() as billingAddress, so the element must not collect them itself.
        // The override has to be GLOBAL: the runtime ignores the per-method
        // fields.card.billingDetails scoping the types advertise and throws a
        // double-collection IntegrationError at confirm (verified E2E 2026-07-13).
        // Owning country forces owning postalCode too; redirect methods (PayPal)
        // collect their billing details on their own pages.
        fields: {
          billingDetails: {
            name: 'never',
            address: { country: 'never', postalCode: 'never' },
          },
        },
      })
      activePaymentElement = paymentElement
      paymentElement.on('ready', () => {
        if (activePaymentElement !== paymentElement) return
        paymentElementReady.value = true
      })
      paymentElement.on('loaderror', (event) => {
        if (activePaymentElement !== paymentElement) return
        checkoutError.value =
          event.error?.message ??
          t('Dashboard.UpgradeModal.Checkout.ErrorStart')
      })
      paymentElement.on('change', async (event) => {
        if (activePaymentElement !== paymentElement) return

        selectedPaymentMethod.value = event.value.type
        if (event.value.type !== 'card') nameError.value = null

        if (
          event.complete &&
          event.value.type === 'card' &&
          !autoAdvanced &&
          !cardholderName.value.trim()
        ) {
          await nextTick()
          const input = document.getElementById(
            'upgrade-cardholder-name'
          ) as HTMLInputElement | null
          if (
            activePaymentElement !== paymentElement ||
            selectedPaymentMethod.value !== 'card' ||
            !input?.isConnected
          ) {
            return
          }
          input.focus()
          if (document.activeElement === input) autoAdvanced = true
        }
      })
      paymentElement.mount(paymentContainer)
    } catch {
      if (!canContinueMount()) return
      destroyCheckoutElements()
      checkout = null
      checkoutError.value = t('Dashboard.UpgradeModal.Checkout.ErrorStart')
      step.value = 'pitch'
    }
  }

  /** Re-reads the plan until the webhook has flipped it to paid (bounded). */
  const pollPlanUntilPaid = async () => {
    for (let attempt = 0; attempt < PLAN_POLL_ATTEMPTS; attempt++) {
      await store.dispatch('fetchDonorPlan')
      if (store.state.donor.swiftaidGivingPlan === 'paid') return
      await new Promise((resolve) => setTimeout(resolve, PLAN_POLL_INTERVAL_MS))
    }
  }

  const finishSuccessfulPayment = async () => {
    // The payment itself is confirmed; the plan flip arrives via webhook moments later.
    step.value = 'success'
    await pollPlanUntilPaid()
    await refreshDataAfterUpgrade()
  }

  // return_url is owned by the client: the session is created WITHOUT one (Stripe rejects
  // it in both places), so we supply it here from our own origin — a redirect-based method
  // then returns to the environment the donor is actually on (dev vs prod).
  const checkoutReturnUrl = () =>
    `${window.location.origin}${window.location.pathname}?upgrade=success`

  /**
   * Confirms a wallet payment authorised through the Express Checkout Element. Fired by
   * the element's 'confirm' event: the wallet already carries its own billing details, so
   * we pass the confirm event straight through (no name/country of ours). paymentFailed()
   * surfaces the error back in the wallet sheet; the checkout step stays put for a retry.
   */
  const confirmExpressPayment = async (
    event: StripeExpressCheckoutElementConfirmEvent
  ) => {
    const method = event.expressPaymentType
    if (!checkout) {
      event.paymentFailed({ reason: 'fail' })
      checkoutError.value = t('Dashboard.UpgradeModal.Checkout.ErrorPay')
      return
    }

    checkoutError.value = null
    nameError.value = null
    isProcessing.value = true
    let stage: 'loadActions' | 'confirm' = 'loadActions'
    try {
      const res = await checkout.loadActions()
      if (res.type !== 'success') {
        event.paymentFailed({ reason: 'fail' })
        checkoutError.value =
          res.error.message ?? t('Dashboard.UpgradeModal.Checkout.ErrorPay')
        reportCheckoutFailure(stage, method)
        return
      }
      stage = 'confirm'
      const result = await res.actions.confirm({
        returnUrl: checkoutReturnUrl(),
        redirect: 'if_required',
        expressCheckoutConfirmEvent: event,
      })
      if (result.type === 'error') {
        event.paymentFailed({ reason: 'fail' })
        checkoutError.value =
          result.error.message ?? t('Dashboard.UpgradeModal.Checkout.ErrorPay')
        if (result.error.code !== 'paymentFailed') {
          reportCheckoutFailure(stage, method)
        }
        return
      }
      await finishSuccessfulPayment()
    } catch (error) {
      event.paymentFailed({ reason: 'fail' })
      checkoutError.value =
        (error as { message?: string })?.message ??
        t('Dashboard.UpgradeModal.Checkout.ErrorPay')
      reportCheckoutFailure(stage, method)
    } finally {
      isProcessing.value = false
    }
  }

  /**
   * Confirms the payment in-modal with the mounted Payment Element. Stays on the
   * checkout step on declines (the same client secret is retryable); redirect-based
   * payment methods return via ?upgrade=success&redirect_status=… (useUpgradeReturn).
   */
  const confirmPayment = async () => {
    const method = selectedPaymentMethod.value
    if (!method) {
      checkoutError.value = t('Dashboard.UpgradeModal.Checkout.ErrorPay')
      isProcessing.value = false
      return
    }

    const name = cardholderName.value.trim()
    const country = cardholderCountry.value
    if (method === 'card' && !name) {
      checkoutError.value = null
      nameError.value = t('Dashboard.UpgradeModal.Checkout.ErrorName')
      isProcessing.value = false
      focusCardholderName()
      return
    }

    nameError.value = null
    if (!checkout || !activePaymentElement) {
      checkoutError.value = t('Dashboard.UpgradeModal.Checkout.ErrorPay')
      isProcessing.value = false
      return
    }

    checkoutError.value = null
    isProcessing.value = true
    const paymentElement = activePaymentElement
    let stage: 'loadActions' | 'confirm' = 'loadActions'
    let readOnlyApplied = false
    let confirmationSucceeded = false
    try {
      paymentElement.update({ readOnly: true })
      readOnlyApplied = true
      // loadActions() hands back the imperative Checkout actions; confirm() submits the
      // mounted Payment Element against the session.
      const res = await checkout.loadActions()
      if (res.type !== 'success') {
        checkoutError.value =
          res.error.message ?? t('Dashboard.UpgradeModal.Checkout.ErrorPay')
        reportCheckoutFailure(stage, method)
        return
      }
      // redirect:'if_required' keeps card/Link in-modal; redirect returns (see
      // checkoutReturnUrl) are handled by useUpgradeReturn.
      stage = 'confirm'
      const result = await res.actions.confirm({
        returnUrl: checkoutReturnUrl(),
        redirect: 'if_required',
        ...(method === 'card'
          ? { billingAddress: { name, address: { country } } }
          : {}),
      })
      if (result.type === 'error') {
        checkoutError.value =
          result.error.message ?? t('Dashboard.UpgradeModal.Checkout.ErrorPay')
        if (result.error.code !== 'paymentFailed') {
          reportCheckoutFailure(stage, method)
        }
        return
      }
      confirmationSucceeded = true
      // Success: result.session.status is typically { type: 'complete',
      // paymentStatus: 'paid' }. The plan flip still arrives via webhook, so poll.
      await finishSuccessfulPayment()
    } catch (error) {
      // Surface the real Stripe error (e.g. an IntegrationError) rather than swallowing it.
      checkoutError.value =
        (error as { message?: string })?.message ??
        t('Dashboard.UpgradeModal.Checkout.ErrorPay')
      reportCheckoutFailure(stage, method)
    } finally {
      if (
        readOnlyApplied &&
        !confirmationSucceeded &&
        activePaymentElement === paymentElement
      ) {
        paymentElement.update({ readOnly: false })
      }
      isProcessing.value = false
    }
  }

  const confirmMockPayment = async () => {
    if (!sessionId.value) return

    checkoutError.value = null
    isProcessing.value = true
    try {
      await store.dispatch('completePaymentSession', sessionId.value)
      await refreshDataAfterUpgrade()
      step.value = 'success'
    } catch {
      checkoutError.value = t('Dashboard.UpgradeModal.Checkout.ErrorPay')
      step.value = 'checkout'
    } finally {
      isProcessing.value = false
    }
  }

  // Confirms the upgrade once the donor is back on the dashboard, since the
  // modal's success step has just been dismissed.
  const finishSuccess = () => {
    isOpen.value = false
    resetModal()
    showToast(t('Dashboard.Toast.UpgradeComplete'))
  }

  const backToPitch = () => {
    step.value = 'pitch'
    destroyCheckoutElements()
    sessionId.value = null
    clientSecret.value = null
    checkoutError.value = null
    resetSelectedPaymentMethod()
    isProcessing.value = false
    paymentElementReady.value = false
    expressCheckoutAvailable.value = false
    cardholderName.value = ''
    cardholderCountry.value = 'GB'
    checkout = null
  }

  const clearNameError = () => {
    nameError.value = null
  }

  return {
    isOpen,
    step,
    sessionId,
    checkoutError,
    nameError,
    isProcessing,
    paymentElementReady,
    expressCheckoutAvailable,
    selectedPaymentMethod,
    cardholderName,
    cardholderCountry,
    isMockCheckout: isMockStripeCheckout(),
    openUpgradeModal,
    closeUpgradeModal,
    startCheckout,
    mountCheckoutElements,
    confirmPayment,
    clearNameError,
    confirmMockPayment,
    finishSuccess,
    backToPitch,
    /** @deprecated Use startCheckout, kept for gradual migration */
    confirmUpgrade: startCheckout,
  }
}
